import React, { useContext, useEffect, useRef, useState, useCallback } from 'react'

// types
import { CategoriesResponse, GetSalonsDataParams, Salon } from '../../../../types/types'

// utils
import { AppContext } from '../../../../utils/appProvider'
import { MyLocationContext } from '../../../../utils/myLocationProvider'
import { SELECTED_CATEGORY_SALONS_PARAMS } from '../../utils'
import { getSelectItemEvent } from '../../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../../utils/dataLayer'

// styles
import { HomePageContainer } from '../../HomePageStyles'

// components
import SalonsSlider from '../SalonsSlider/SalonsSlider'

// hooks
import useMessages from '../../../../hooks/useMessages'

type Props = {
	selectedCategorySalonsData: Salon[] | undefined
	selectedCategoryData: CategoriesResponse['categories'][number]
}

// component
const SelectedCategorySalonsSection = (props: Props) => {
	const { selectedCategorySalonsData, selectedCategoryData } = props

	const { apiBrowser } = useContext(AppContext)
	const { myLocation, isLoadingMyLocation } = useContext(MyLocationContext)

	const { messages } = useMessages()

	const title = selectedCategoryData?.name || messages['selected-category-salons-section-title-fallback']

	// NOTE: undefined means loading error
	const [selectedCategorySalons, setSelectedCategorySalons] = useState<Salon[] | undefined>(selectedCategorySalonsData)

	const [isLoadingSelectedCategorySalons, setIsLoadingSelectedCategorySalons] = useState(isLoadingMyLocation)

	const isSelectedCategorySalonsMountFetchRef = useRef(true)
	const isMyPositionKnownOnMount = useRef(!!myLocation)

	const fetchSelectedCategorySalons = useCallback(async () => {
		try {
			const salonsDataParams: GetSalonsDataParams = {
				...SELECTED_CATEGORY_SALONS_PARAMS,
				latMy: myLocation?.latMy,
				lonMy: myLocation?.lonMy
			}
			const newSalonsData = await apiBrowser.b2c.getSalonsData({ ...salonsDataParams })
			setSelectedCategorySalons(newSalonsData.salons)
		} catch {
			setSelectedCategorySalons(undefined)
		}
	}, [myLocation?.latMy, myLocation?.lonMy, apiBrowser])

	useEffect(() => {
		/**
		 * Salons are already fetched on the server side, so client-side fetching is not always necessary on page load.
		 * However, since we are working with `myLocation`, which is not available on the server, we need to handle location-based fetching.
		 * If the user grants permission to access their location, we want to refetch the data using the user's location.
		 * During this process, we show an initial loading state until the location is resolved, and if needed, fetch new data.
		 * If the user's location is already known from query parameters at page initialization, there is no need to show the initial loading state.
		 * The loading state is displayed over the salons fetched from the server, ensuring that the data is available in the HTML for search engine bots, while preventing a content flash for users.
		 */
		;(async () => {
			// NOTE: uncomment if you want to refetch salons based on user's location when user allow his position in Booking form
			/* if (myLocation) {
				isSelectedCategorySalonsMountFetchRef.current = true
			} */

			if (isLoadingMyLocation || !isSelectedCategorySalonsMountFetchRef.current) {
				return
			}

			if (myLocation && !isMyPositionKnownOnMount.current) {
				setIsLoadingSelectedCategorySalons(true)
				await fetchSelectedCategorySalons()
			}

			setIsLoadingSelectedCategorySalons(false)
			isSelectedCategorySalonsMountFetchRef.current = false
		})()
	}, [isLoadingMyLocation, myLocation, fetchSelectedCategorySalons])

	const onTryAgain = async () => {
		setIsLoadingSelectedCategorySalons(true)
		await fetchSelectedCategorySalons()
		setIsLoadingSelectedCategorySalons(false)
	}

	return (
		<HomePageContainer>
			<SalonsSlider
				title={title}
				salons={selectedCategorySalons || []}
				isLoadingData={isLoadingSelectedCategorySalons}
				isLoadingError={selectedCategorySalons === undefined}
				imageLoading={'lazy'}
				errorScreen={{
					button: {
						onClick: onTryAgain
					}
				}}
				onSliderItemClick={(salon, listPosition) => {
					const event = getSelectItemEvent({ listPosition, salon, listName: 'salons_hairdresser_women' })
					pushToDataLayer(event)
				}}
			/>
		</HomePageContainer>
	)
}

export default SelectedCategorySalonsSection
